import PropTypes from 'prop-types';
import { Flex } from 'antd';
import { useIntl } from 'react-intl';
import { useNavigate, useOutletContext } from 'react-router-dom';
import AntdSelect from '../../../../../../../../../components/AntdSelect';
import AccionaButton from '../../../../../../../../../components/AccionaButton';
import { useContext, useState } from 'react';
import { downloadPDF } from '../../../../../../../../../services/api/normative';
import { downloadXlsx } from '../../../../../../../../../services/api/deliveryNote';
import { NotificationsContext } from '../../../../../../../../../context/NotificationsContext';

const TraceabilityTableHeader = ({
  plants,
  selectedPlant,
  setSelectedPlant,
  params,
  onParamsChange,
  setDidFilter,
  selectedRows,
  normative,
  setParams,
  onEditOnBulkClick,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { project } = useOutletContext();
  const { pushNotification } = useContext(NotificationsContext);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);

  return (
    <Flex wrap={false} justify="space-between" align="center" gap={10}>
      <Flex justify="flex-start" gap={10}>
        <AntdSelect
          options={plants?.map(({ plant, supplier_id }) => ({
            label: `${plant} - ${supplier_id}`,
            value: `${plant}:::${supplier_id}`,
          }))}
          style={{ width: 300, height: 52 }}
          size="large"
          placeholder={intl.formatMessage({
            id: 'pages.quality.normative.traceability.select.placeholder',
          })}
          theme="white"
          value={
            selectedPlant
              ? `${selectedPlant?.plant}:::${selectedPlant?.supplier_id}`
              : undefined
          }
          onChange={(plant) => {
            setSelectedPlant({
              plant: plant.split(':::')[0],
              supplier_id: plant.split(':::')[1],
            });
            setParams((prev) => ({
              ...prev.params,
              filters: {
                sortable: 'arriving_date-ASC',
                plant: {
                  plant: plant.split(':::')[0],
                  supplier_id: plant.split(':::')[1],
                },
              },
            }));
            onParamsChange(params);
            setDidFilter(undefined);
          }}
        />
        <AccionaButton
          style={{ padding: 15 }}
          id="export-traceability-pdf"
          type="white"
          msg="pdf"
          loading={downloadingPDF}
          onClick={() => {
            setDownloadingPDF(true);
            downloadPDF(project.id_project)
              .then(() => {
                setDownloadingPDF(false);
                pushNotification({
                  message: intl.formatMessage({
                    id: 'pages.quality.normative.traceability.export.success',
                  }),
                  type: 'success',
                });
              })
              .catch((error) => {
                setDownloadingPDF(false);
                pushNotification({
                  message: intl.formatMessage({
                    id: 'pages.quality.normative.traceability.export.error',
                  }),
                  type: 'error',
                });
              });
          }}
          classIcon="icon fas fa-download"
        />
        <AccionaButton
          style={{ padding: 15 }}
          id="export-traceability-xls"
          type="white"
          msg="xlsx"
          loading={downloadingXlsx}
          onClick={() => {
            setDownloadingXlsx(true);
            downloadXlsx({
              ...params,
              workcode: project?.work_code,
            })
              .then(() => {
                setDownloadingXlsx(false);
                pushNotification({
                  message: intl.formatMessage({
                    id: 'pages.quality.normative.traceability.export.success',
                  }),
                  type: 'success',
                });
              })
              .catch(() => {
                setDownloadingXlsx(false);
                pushNotification({
                  message: intl.formatMessage({
                    id: 'pages.quality.normative.traceability.export.error',
                  }),
                  type: 'error',
                });
              });
          }}
          classIcon="icon fas fa-download"
        />
        {selectedRows.length > 1 && (
          <AccionaButton
            style={{ padding: 15 }}
            id="edit-on-bulk"
            type="primary"
            msg={intl.formatMessage({
              id: 'pages.quality.normative.traceability.editOnBulk',
            })}
            loading={downloadingXlsx}
            onClick={onEditOnBulkClick}
            classIcon="icon fas fa-edit"
          />
        )}
      </Flex>
      <Flex>
        <AccionaButton
          id="statisticalControl"
          style={{ padding: 15 }}
          type="primary"
          onClick={() => {
            navigate(`../statistical-control`);
          }}
          msg={intl.formatMessage({ id: 'button.statisticalControl' })}
          classIcon="icon fas fa-chart-bar"
        />
        <AccionaButton
          style={{ padding: 15 }}
          id="100%Control"
          type="primary"
          onClick={() => {
            navigate('../100-control');
          }}
          msg={intl.formatMessage({ id: 'button.100%Control' })}
          classIcon="icon fas fa-percentage"
        />
      </Flex>
    </Flex>
  );
};

TraceabilityTableHeader.propTypes = {
  plants: PropTypes.arrayOf(
    PropTypes.shape({
      plant: PropTypes.string,
      supplier_id: PropTypes.string,
    }),
  ),
  selectedPlant: PropTypes.shape({
    plant: PropTypes.string,
    supplier_id: PropTypes.string,
  }),
  setSelectedPlant: PropTypes.func.isRequired,
  params: PropTypes.object,
  onParamsChange: PropTypes.func.isRequired,
  setDidFilter: PropTypes.func,
  selectedRows: PropTypes.array,
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  setParams: PropTypes.func.isRequired,
  onEditOnBulkClick: PropTypes.func.isRequired,
};

TraceabilityTableHeader.defaultProps = {
  plants: [],
  selectedPlant: null,
  setDidFilter: () => {},
  selectedRows: [],
  params: undefined,
};

export default TraceabilityTableHeader;
